<template>
  <div class="aa-page">
    <div class="aa-top">
      <h1>合作夥伴管理</h1>
      <div class="aa-top-right">
        <button class="aa-copy-btn" @click="copyApplyLink">{{ copiedApply ? '已複製！' : '複製夥伴申請連結' }}</button>
        <button class="aa-copy-btn" @click="copyLoginLink">{{ copiedLogin ? '已複製！' : '複製夥伴登入連結' }}</button>
      </div>
    </div>

    <!-- 篩選 -->
    <div class="aa-filters">
      <button v-for="s in statusOptions" :key="s.val"
              class="aa-filter-btn" :class="{ active: filterStatus === s.val }"
              @click="filterStatus = s.val">{{ s.label }}</button>
    </div>

    <!-- 合作夥伴列表 -->
    <div class="aa-table-wrap">
      <table class="aa-table">
        <thead>
          <tr>
            <th>姓名 / Email</th>
            <th>狀態</th>
            <th>推廣碼</th>
            <th>分潤比例</th>
            <th>訂單數</th>
            <th>累計業績</th>
            <th>已提領</th>
            <th>申請時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in filteredList" :key="a.id">
            <td>
              <div class="aa-name">{{ a.name }}</div>
              <div class="aa-email">{{ a.email }}</div>
            </td>
            <td><span class="aa-badge" :class="a.status">{{ statusLabel(a.status) }}</span></td>
            <td class="aa-code">{{ a.ref_code || '—' }}</td>
            <td>
              <div v-if="editRateId !== a.id" class="aa-rate-row">
                {{ a.commission_rate }}%
                <button class="aa-btn-xs" @click="startEditRate(a)">改</button>
              </div>
              <div v-else class="aa-rate-edit">
                <input v-model="editRateVal" type="number" min="0" max="100" step="0.1" class="aa-rate-input" />
                <button class="aa-btn-xs primary" @click="saveRate(a.id)">存</button>
                <button class="aa-btn-xs" @click="editRateId=null">✕</button>
              </div>
            </td>
            <td>{{ a.total_orders }}</td>
            <td>NT$ {{ fmtNum(a.total_sales) }}</td>
            <td>NT$ {{ fmtNum(a.total_paid) }}</td>
            <td>{{ fmtDate(a.created_at) }}</td>
            <td>
              <div class="aa-actions">
                <button v-if="a.status === 'pending'" class="aa-btn-sm primary" @click="approve(a)">核准</button>
                <button v-if="a.status === 'pending'" class="aa-btn-sm danger" @click="reject(a)">拒絕</button>
                <button v-if="a.status === 'approved'" class="aa-btn-sm warning" @click="toggleSuspend(a)">停用</button>
                <button v-if="a.status === 'suspended'" class="aa-btn-sm" @click="toggleSuspend(a)">啟用</button>
                <button class="aa-btn-sm info" @click="viewStats(a)">詳情</button>
                <button class="aa-btn-sm" @click="viewOrders(a)">訂單</button>
                <button class="aa-btn-sm delete" @click="deleteAffiliate(a)">刪除</button>
              </div>
            </td>
          </tr>
          <tr v-if="filteredList.length === 0">
            <td colspan="9" class="aa-empty">無資料</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 訂單 Modal -->
    <div v-if="showOrders" class="aa-modal-overlay" @click.self="showOrders=false">
      <div class="aa-modal">
        <div class="aa-modal-header">
          <span>{{ selectedAffiliate?.name }} 的分潤訂單</span>
          <button @click="showOrders=false">✕</button>
        </div>
        <div class="aa-modal-body">
          <div v-if="modalOrders.length === 0" class="aa-empty">無訂單</div>
          <table v-else class="aa-table">
            <thead>
              <tr>
                <th>訂單編號</th>
                <th>訂單狀況</th>
                <th>商品金額</th>
                <th>分潤金額</th>
                <th>分潤狀態</th>
                <th>取貨日</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="o in modalOrders" :key="o.id">
                <td>#{{ o.order_id }}</td>
                <td>
                  <span class="aa-badge" :class="orderBadgeClass(o.order_status)">
                    {{ orderStatusCnLabel(o.order_status) }}
                  </span>
                </td>
                <td>NT$ {{ fmtNum(o.order_amount) }}</td>
                <td>NT$ {{ fmtNum(o.commission_amount - o.refund_commission) }}</td>
                <td><span class="aa-badge" :class="o.status">{{ orderStatusLabel(o.status) }}</span></td>
                <td>{{ o.delivered_at ? fmtDate(o.delivered_at) : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 夥伴詳情 Modal -->
    <div v-if="showStats" class="aa-modal-overlay" @click.self="showStats=false">
      <div class="aa-modal" style="max-width:560px">
        <div class="aa-modal-header">
          <span>{{ statsAffiliate?.name }} 的詳細資訊</span>
          <button @click="showStats=false">✕</button>
        </div>
        <div class="aa-modal-body" v-if="statsData">
          <!-- 基本資料 -->
          <div class="aa-stats-section">
            <div class="aa-stats-title">基本資料</div>
            <div class="aa-info-grid">
              <div class="aa-info-item"><span class="aa-info-label">姓名</span><span>{{ statsAffiliate?.name }}</span></div>
              <div class="aa-info-item"><span class="aa-info-label">帳號</span><span>{{ statsAffiliate?.username || '—' }}</span></div>
              <div class="aa-info-item"><span class="aa-info-label">Email</span><span>{{ statsAffiliate?.email }}</span></div>
              <div class="aa-info-item"><span class="aa-info-label">手機</span><span>{{ statsAffiliate?.phone || '—' }}</span></div>
              <div class="aa-info-item"><span class="aa-info-label">推廣碼</span><span class="aa-code">{{ statsAffiliate?.ref_code || '—' }}</span></div>
              <div class="aa-info-item"><span class="aa-info-label">分潤比例</span><span>{{ statsAffiliate?.commission_rate }}%</span></div>
            </div>
          </div>

          <!-- 財務統計（仿夥伴後台） -->
          <div class="aa-stats-section">
            <div class="aa-stats-title">財務統計</div>
            <div class="aa-stat-cards">
              <div class="aa-stat-card balance">
                <div class="aa-stat-label">可提領餘額</div>
                <div class="aa-stat-val green">NT$ {{ fmtNum(statsData.balance) }}</div>
              </div>
              <div class="aa-stat-card">
                <div class="aa-stat-label">預估分潤（待確認）</div>
                <div class="aa-stat-val orange">NT$ {{ fmtNum(statsData.pending_commission) }}</div>
              </div>
              <div class="aa-stat-card">
                <div class="aa-stat-label">已確認分潤</div>
                <div class="aa-stat-val">NT$ {{ fmtNum(statsData.confirmed_commission) }}</div>
              </div>
              <div class="aa-stat-card">
                <div class="aa-stat-label">累計銷售金額</div>
                <div class="aa-stat-val">NT$ {{ fmtNum(statsData.total_sales) }}</div>
              </div>
              <div class="aa-stat-card">
                <div class="aa-stat-label">累計訂單數</div>
                <div class="aa-stat-val">{{ statsData.total_orders }}</div>
              </div>
              <div class="aa-stat-card">
                <div class="aa-stat-label">已提領總額</div>
                <div class="aa-stat-val">NT$ {{ fmtNum(statsData.total_paid) }}</div>
              </div>
              <div class="aa-stat-card">
                <div class="aa-stat-label">待處理提領</div>
                <div class="aa-stat-val orange">NT$ {{ fmtNum(statsData.pending_payout) }}</div>
              </div>
            </div>
          </div>

          <!-- 銀行帳戶 -->
          <div class="aa-stats-section">
            <div class="aa-stats-title">銀行帳戶</div>
            <div class="aa-info-grid">
              <div class="aa-info-item"><span class="aa-info-label">銀行代碼</span><span>{{ statsAffiliate?.bank_code || '未填' }}</span></div>
              <div class="aa-info-item"><span class="aa-info-label">帳號</span><span>{{ statsAffiliate?.bank_account || '未填' }}</span></div>
              <div class="aa-info-item"><span class="aa-info-label">戶名</span><span>{{ statsAffiliate?.bank_holder || '未填' }}</span></div>
            </div>
          </div>

          <!-- 修改 Email -->
          <div class="aa-stats-section">
            <div class="aa-stats-title">修改 Email</div>
            <div class="aa-reset-row">
              <input v-model="editEmailVal" type="email" placeholder="輸入正確的 Email" class="aa-reset-input" />
              <button class="aa-btn-sm primary" :disabled="editEmailLoading" @click="adminUpdateEmail(statsAffiliate?.id)">
                {{ editEmailLoading ? '更新中...' : '確認更新' }}
              </button>
            </div>
            <div v-if="editEmailMsg" :class="['aa-reset-msg', editEmailOk ? 'ok' : 'err']">{{ editEmailMsg }}</div>
          </div>

          <!-- 重設密碼 -->
          <div class="aa-stats-section">
            <div class="aa-stats-title">重設登入密碼</div>
            <div class="aa-reset-row">
              <input v-model="resetPwVal" type="text" placeholder="輸入新密碼（至少 6 碼）" class="aa-reset-input" />
              <button class="aa-btn-sm danger" :disabled="resetPwLoading" @click="adminResetPw(statsAffiliate?.id)">
                {{ resetPwLoading ? '處理中...' : '確認重設' }}
              </button>
            </div>
            <div v-if="resetPwMsg" :class="['aa-reset-msg', resetPwOk ? 'ok' : 'err']">{{ resetPwMsg }}</div>
          </div>
        </div>
        <div v-else class="aa-modal-body aa-empty">載入中...</div>
      </div>
    </div>

    <!-- Approve Modal（設定分潤比例）-->
    <div v-if="approveModal" class="aa-modal-overlay" @click.self="approveModal=false">
      <div class="aa-modal small">
        <div class="aa-modal-header">
          <span>核准 {{ approvingAffiliate?.name }}</span>
          <button @click="approveModal=false">✕</button>
        </div>
        <div class="aa-modal-body">
          <div class="aa-field">
            <label>分潤比例（%）</label>
            <input v-model="approveRate" type="number" min="0" max="100" step="0.1" placeholder="預設 10%" />
          </div>
          <div class="aa-modal-actions">
            <button class="aa-btn-sm primary" @click="confirmApprove" :disabled="approveLoading">
              {{ approveLoading ? '處理中...' : '確認核准' }}
            </button>
            <button class="aa-btn-sm" @click="approveModal=false">取消</button>
          </div>
          <div v-if="approveMsg" class="aa-msg">{{ approveMsg }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'

const affiliates = ref([])
const filterStatus = ref('all')
const editRateId = ref(null)
const editRateVal = ref('')
const showOrders = ref(false)
const selectedAffiliate = ref(null)
const modalOrders = ref([])
const showStats = ref(false)
const statsAffiliate = ref(null)
const statsData = ref(null)
const resetPwVal = ref('')
const resetPwLoading = ref(false)
const resetPwMsg = ref('')
const resetPwOk = ref(false)
const editEmailVal = ref('')
const editEmailLoading = ref(false)
const editEmailMsg = ref('')
const editEmailOk = ref(false)
const approveModal = ref(false)
const approvingAffiliate = ref(null)
const approveRate = ref('10')
const approveLoading = ref(false)
const approveMsg = ref('')
const copiedApply = ref(false)
const copiedLogin = ref(false)

const statusOptions = [
  { val: 'all', label: '全部' },
  { val: 'pending', label: '待審核' },
  { val: 'approved', label: '已核准' },
  { val: 'suspended', label: '已停用' },
  { val: 'rejected', label: '已拒絕' },
]

const filteredList = computed(() => {
  if (filterStatus.value === 'all') return affiliates.value
  return affiliates.value.filter(a => a.status === filterStatus.value)
})

async function load() {
  try {
    const res = await axios.get('/api/admin/affiliate/affiliates', { withCredentials: true })
    if (res.data.success) affiliates.value = res.data.affiliates
  } catch (e) { console.error(e) }
}

function approve(a) {
  approvingAffiliate.value = a
  approveRate.value = a.commission_rate
  approveMsg.value = ''
  approveModal.value = true
}

async function confirmApprove() {
  approveLoading.value = true
  approveMsg.value = ''
  try {
    const res = await axios.put(`/api/admin/affiliate/affiliates/${approvingAffiliate.value.id}/approve`,
      { commission_rate: approveRate.value || null }, { withCredentials: true })
    if (res.data.success) {
      approveMsg.value = `已核准，推廣碼：${res.data.ref_code}`
      await load()
      setTimeout(() => { approveModal.value = false }, 1500)
    } else {
      approveMsg.value = res.data.message
    }
  } catch (e) {
    approveMsg.value = e.response?.data?.message || '操作失敗'
  } finally {
    approveLoading.value = false
  }
}

async function reject(a) {
  const note = window.prompt(`拒絕 ${a.name} 的申請，請輸入備註（選填）：`)
  if (note === null) return
  try {
    await axios.put(`/api/admin/affiliate/affiliates/${a.id}/reject`, { note }, { withCredentials: true })
    await load()
  } catch (e) { alert('操作失敗') }
}

async function toggleSuspend(a) {
  try {
    const res = await axios.put(`/api/admin/affiliate/affiliates/${a.id}/suspend`, {}, { withCredentials: true })
    if (res.data.success) await load()
  } catch (e) { alert('操作失敗') }
}

function startEditRate(a) {
  editRateId.value = a.id
  editRateVal.value = a.commission_rate
}

async function saveRate(id) {
  if (!editRateVal.value) return
  try {
    await axios.put(`/api/admin/affiliate/affiliates/${id}/rate`,
      { commission_rate: editRateVal.value }, { withCredentials: true })
    editRateId.value = null
    await load()
  } catch (e) { alert('更新失敗') }
}

async function viewOrders(a) {
  selectedAffiliate.value = a
  showOrders.value = true
  modalOrders.value = []
  try {
    const res = await axios.get(`/api/admin/affiliate/affiliates/${a.id}/orders`, { withCredentials: true })
    modalOrders.value = res.data.orders || []
  } catch (e) { console.error(e) }
}

async function viewStats(a) {
  statsAffiliate.value = a
  statsData.value = null
  showStats.value = true
  resetPwVal.value = ''
  resetPwMsg.value = ''
  editEmailVal.value = ''
  editEmailMsg.value = ''
  try {
    const res = await axios.get(`/api/admin/affiliate/affiliates/${a.id}/stats`, { withCredentials: true })
    if (res.data.success) {
      statsAffiliate.value = res.data.affiliate
      statsData.value = res.data.stats
    }
  } catch (e) { console.error(e) }
}

async function adminUpdateEmail(id) {
  if (!editEmailVal.value || !editEmailVal.value.includes('@')) {
    editEmailMsg.value = '請輸入正確的 Email 格式'
    editEmailOk.value = false
    return
  }
  editEmailLoading.value = true
  editEmailMsg.value = ''
  try {
    const res = await axios.put(`/api/admin/affiliate/affiliates/${id}/email`,
      { email: editEmailVal.value }, { withCredentials: true })
    if (res.data.success) {
      editEmailMsg.value = 'Email 已更新成功'
      editEmailOk.value = true
      statsAffiliate.value = { ...statsAffiliate.value, email: editEmailVal.value }
      editEmailVal.value = ''
      await load()
    } else {
      editEmailMsg.value = res.data.message || '更新失敗'
      editEmailOk.value = false
    }
  } catch (e) {
    editEmailMsg.value = e.response?.data?.message || '更新失敗'
    editEmailOk.value = false
  } finally {
    editEmailLoading.value = false
  }
}

async function adminResetPw(id) {
  if (!resetPwVal.value || resetPwVal.value.length < 6) {
    resetPwMsg.value = '密碼至少需要 6 碼'
    resetPwOk.value = false
    return
  }
  resetPwLoading.value = true
  resetPwMsg.value = ''
  try {
    await axios.put(`/api/admin/affiliate/affiliates/${id}/reset-password`,
      { password: resetPwVal.value }, { withCredentials: true })
    resetPwMsg.value = '密碼已重設成功'
    resetPwOk.value = true
    resetPwVal.value = ''
  } catch (e) {
    resetPwMsg.value = e.response?.data?.message || '重設失敗'
    resetPwOk.value = false
  } finally {
    resetPwLoading.value = false
  }
}

async function deleteAffiliate(a) {
  const ok = window.confirm(`確定要刪除「${a.name}」嗎？\n此操作將一併刪除其分潤訂單與提領紀錄，且無法復原。`)
  if (!ok) return
  try {
    await axios.delete(`/api/admin/affiliate/affiliates/${a.id}`, { withCredentials: true })
    await load()
  } catch (e) { alert(e.response?.data?.message || '刪除失敗') }
}

function copyApplyLink() {
  navigator.clipboard.writeText('https://jiliskin.com/affiliate/apply').then(() => {
    copiedApply.value = true
    setTimeout(() => copiedApply.value = false, 2000)
  })
}

function copyLoginLink() {
  navigator.clipboard.writeText('https://jiliskin.com/affiliate/login').then(() => {
    copiedLogin.value = true
    setTimeout(() => copiedLogin.value = false, 2000)
  })
}

function statusLabel(s) {
  const map = { pending: '待審核', approved: '已核准', rejected: '已拒絕', suspended: '已停用' }
  return map[s] || s
}
function orderStatusLabel(s) {
  const map = { pending: '待確認', confirmed: '已確認', refunded: '退款' }
  return map[s] || s
}
const ORDER_STATUS_MAP = {
  pending:                   { label: '未處理',     cls: 'pending'   },
  processing:                { label: '處理中',     cls: 'pending'   },
  processing_received:       { label: '處理中',     cls: 'pending'   },
  waiting_shipment:          { label: '待出貨',     cls: 'pending'   },
  logistics_check:           { label: '物流驗收',   cls: 'pending'   },
  delivering:                { label: '配送中',     cls: 'pending'   },
  waiting_pickup:            { label: '待取貨',     cls: 'confirmed' },
  picked:                    { label: '已取貨',     cls: 'confirmed' },
  canceled:                  { label: '已取消',     cls: 'refunded'  },
  normal_return:             { label: '一般退貨',   cls: 'refunded'  },
  buyer_not_pick:            { label: '買家未取',   cls: 'refunded'  },
  lost_package:              { label: '包裹遺失',   cls: 'refunded'  },
  abandon_package:           { label: '包裹拋棄',   cls: 'refunded'  },
  factory_return:            { label: '廠退',       cls: 'refunded'  },
  seller_not_pick:           { label: '賣家未取',   cls: 'refunded'  },
  abnormal_order:            { label: '異常訂單',   cls: 'refunded'  },
}
function orderBadgeClass(s) {
  return ORDER_STATUS_MAP[s]?.cls || 'pending'
}
function orderStatusCnLabel(s) {
  return ORDER_STATUS_MAP[s]?.label || s || '—'
}
function fmtNum(n) { return Math.floor(Number(n || 0)).toLocaleString('zh-TW') }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('zh-TW') : '-' }

onMounted(load)
</script>

<style scoped>
* { box-sizing: border-box; }
.aa-page { padding: 1.5rem; }
.aa-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.2rem; flex-wrap: wrap; gap: 0.8rem; }
.aa-top h1 { font-size: 1.3rem; color: #2E3A59; margin: 0; }
.aa-top-right { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; }
.aa-copy-btn {
  background: #8B4513; color: #fff; border: none; border-radius: 8px;
  padding: 0.5rem 1.2rem; cursor: pointer; font-size: 0.88rem;
}
.aa-copy-btn:hover { background: #6b3310; }

/* Filters */
.aa-filters { display: flex; gap: 0.5rem; margin-bottom: 1rem; flex-wrap: wrap; }
.aa-filter-btn {
  border: 1px solid #ddd; background: #fff; border-radius: 20px;
  padding: 0.3rem 0.9rem; font-size: 0.83rem; cursor: pointer;
}
.aa-filter-btn.active { background: #2E3A59; color: #fff; border-color: #2E3A59; }

/* Table */
.aa-table-wrap { overflow-x: auto; }
.aa-table { width: 100%; border-collapse: collapse; font-size: 0.87rem; background: #fff; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.aa-table th { background: #f0f2f7; padding: 0.7rem 0.8rem; text-align: left; font-weight: 600; color: #555; white-space: nowrap; }
.aa-table td { padding: 0.65rem 0.8rem; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
.aa-table tr:last-child td { border-bottom: none; }

.aa-name { font-weight: 600; color: #333; }
.aa-email { font-size: 0.78rem; color: #888; }
.aa-code { font-family: monospace; font-size: 0.82rem; color: #555; }

.aa-badge {
  display: inline-block; padding: 0.2rem 0.6rem; border-radius: 12px;
  font-size: 0.78rem; font-weight: 600;
}
.aa-badge.pending { background: #fef9e7; color: #e67e22; }
.aa-badge.approved { background: #eafaf1; color: #27ae60; }
.aa-badge.rejected { background: #fdedec; color: #c0392b; }
.aa-badge.suspended { background: #f8f9fa; color: #888; }
.aa-badge.confirmed { background: #eafaf1; color: #27ae60; }
.aa-badge.refunded { background: #fdedec; color: #c0392b; }

.aa-rate-row { display: flex; align-items: center; gap: 0.4rem; }
.aa-rate-edit { display: flex; align-items: center; gap: 0.3rem; }
.aa-rate-input { width: 60px; border: 1px solid #ddd; border-radius: 6px; padding: 0.25rem 0.4rem; font-size: 0.85rem; }

.aa-actions { display: flex; gap: 0.3rem; flex-wrap: wrap; }
.aa-btn-sm {
  border: 1px solid #ccc; background: #fff; border-radius: 6px;
  padding: 0.25rem 0.7rem; cursor: pointer; font-size: 0.8rem; white-space: nowrap;
}
.aa-btn-sm.primary { background: #27ae60; color: #fff; border-color: #27ae60; }
.aa-btn-sm.danger { background: #e74c3c; color: #fff; border-color: #e74c3c; }
.aa-btn-sm.warning { background: #e67e22; color: #fff; border-color: #e67e22; }
.aa-btn-sm.delete { background: #fff; color: #c0392b; border-color: #e74c3c; }
.aa-btn-sm.delete:hover { background: #e74c3c; color: #fff; }
.aa-btn-sm.info { background: #fff; color: #2980b9; border-color: #3498db; }
.aa-btn-sm.info:hover { background: #3498db; color: #fff; }

/* 詳情 Modal */
.aa-stats-section { margin-bottom: 1.4rem; }
.aa-stats-title { font-size: 0.82rem; font-weight: 700; color: #888; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.7rem; border-bottom: 1px solid #f0f0f0; padding-bottom: 0.4rem; }
.aa-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem 1.2rem; }
.aa-info-item { display: flex; gap: 0.5rem; font-size: 0.88rem; align-items: baseline; }
.aa-info-label { color: #888; min-width: 64px; flex-shrink: 0; font-size: 0.82rem; }
.aa-stat-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 0.7rem; }
.aa-stat-card { background: #f9f9fb; border: 1px solid #ececec; border-radius: 10px; padding: 0.8rem 1rem; text-align: center; }
.aa-stat-label { font-size: 0.76rem; color: #888; margin-bottom: 0.3rem; }
.aa-stat-val { font-size: 1.1rem; font-weight: 700; color: #333; }
.aa-stat-val.green { color: #27ae60; }
.aa-stat-val.orange { color: #e67e22; }
.aa-reset-row { display: flex; gap: 0.6rem; align-items: center; }
.aa-reset-input { flex: 1; border: 1px solid #ddd; border-radius: 8px; padding: 0.45rem 0.8rem; font-size: 0.9rem; outline: none; }
.aa-reset-input:focus { border-color: #e74c3c; }
.aa-reset-msg { margin-top: 0.5rem; font-size: 0.85rem; padding: 0.4rem 0.8rem; border-radius: 6px; }
.aa-reset-msg.ok { background: #f0fff4; color: #27ae60; }
.aa-reset-msg.err { background: #fff0f0; color: #c0392b; }
.aa-btn-xs {
  border: 1px solid #ccc; background: #fff; border-radius: 4px;
  padding: 0.15rem 0.5rem; cursor: pointer; font-size: 0.78rem;
}
.aa-btn-xs.primary { background: #27ae60; color: #fff; border-color: #27ae60; }

.aa-empty { text-align: center; color: #aaa; padding: 2rem; }

/* Modal */
.aa-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 2000;
  display: flex; align-items: center; justify-content: center; padding: 1rem;
}
.aa-modal {
  background: #fff; border-radius: 12px; width: 100%; max-width: 800px;
  max-height: 90vh; display: flex; flex-direction: column;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}
.aa-modal.small { max-width: 380px; }
.aa-modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 1rem 1.2rem; border-bottom: 1px solid #eee; font-weight: 600;
}
.aa-modal-header button { background: none; border: none; font-size: 1.1rem; cursor: pointer; color: #888; }
.aa-modal-body { padding: 1.2rem; overflow-y: auto; }
.aa-modal-actions { display: flex; gap: 0.6rem; margin-top: 1rem; }
.aa-field { display: flex; flex-direction: column; gap: 0.3rem; }
.aa-field label { font-size: 0.83rem; font-weight: 600; color: #555; }
.aa-field input {
  border: 1px solid #ddd; border-radius: 8px; padding: 0.55rem 0.8rem;
  font-size: 0.95rem; outline: none;
}
.aa-field input:focus { border-color: #2E3A59; }
.aa-msg { margin-top: 0.8rem; padding: 0.6rem 0.9rem; background: #f0fff4; color: #27ae60; border-radius: 8px; font-size: 0.88rem; }
</style>
